import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { INACTIVE_ACCOUNT_ALLOWED_POPUP_TYPES } from '../utils/inactiveAccountPopupAllowlist';

const PopupContext = createContext();

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within PopupProvider');
  }
  return context;
};

export const PopupProvider = ({ children }) => {
  const [popups, setPopups] = useState([]);
  /** When true, only settings / account sub-popups may open (deactivated account shell). */
  const [accountPopupsRestricted, setAccountPopupsRestricted] = useState(false);

  const openPopup = useCallback(
    (type, data = {}, priority = 0) => {
      if (accountPopupsRestricted && !INACTIVE_ACCOUNT_ALLOWED_POPUP_TYPES.has(type)) {
        return null;
      }
      const id = `popup-${Date.now()}-${Math.random()}`;
      const popup = {
        id,
        type,
        data,
        priority,
        isOpen: true,
      };

      setPopups((prev) => [...prev, popup]);
      return id;
    },
    [accountPopupsRestricted]
  );

  const closePopup = useCallback((id) => {
    setPopups((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  const closeAllPopups = useCallback(() => {
    setPopups([]);
  }, []);

  const closePopupByType = useCallback((type) => {
    setPopups((prev) => prev.filter((popup) => popup.type !== type));
  }, []);

  const getPopup = useCallback(
    (id) => {
      return popups.find((popup) => popup.id === id);
    },
    [popups]
  );

  const sortedPopups = useMemo(() => [...popups].sort((a, b) => b.priority - a.priority), [popups]);

  const value = useMemo(
    () => ({
      popups: sortedPopups,
      openPopup,
      closePopup,
      closeAllPopups,
      closePopupByType,
      getPopup,
      accountPopupsRestricted,
      setAccountPopupsRestricted,
    }),
    [
      sortedPopups,
      openPopup,
      closePopup,
      closeAllPopups,
      closePopupByType,
      getPopup,
      accountPopupsRestricted,
      setAccountPopupsRestricted,
    ]
  );

  return <PopupContext.Provider value={value}>{children}</PopupContext.Provider>;
};
